import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import ChatMessage from './ChatMessage';

const NEAR_BOTTOM_PX = 80;

function scrollContainerToBottom(el, behavior = 'auto') {
  if (!el) return;
  if (behavior === 'smooth' && el.scrollTo) {
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  } else {
    el.scrollTop = el.scrollHeight;
  }
}

export default function ChatMessageList({ messages, loading, conversationId, scrollToBottomOnSend = 0, onActionResolved }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const prevConversationIdRef = useRef(conversationId);

  const readNearBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  }, []);

  const handleScroll = useCallback(() => {
    stickToBottomRef.current = readNearBottom();
  }, [readNearBottom]);

  useEffect(() => {
    const convChanged = conversationId !== prevConversationIdRef.current;
    prevConversationIdRef.current = conversationId;
    if (!convChanged) return;

    stickToBottomRef.current = true;
    requestAnimationFrame(() => {
      scrollContainerToBottom(containerRef.current, 'auto');
    });
  }, [conversationId]);

  useEffect(() => {
    if (!scrollToBottomOnSend) return;
    stickToBottomRef.current = true;
    requestAnimationFrame(() => {
      scrollContainerToBottom(containerRef.current, 'auto');
    });
  }, [scrollToBottomOnSend]);

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    requestAnimationFrame(() => {
      scrollContainerToBottom(containerRef.current, 'auto');
    });
  }, [messages]);

  if (loading) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          {t('chat.emptyState')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} onScroll={handleScroll} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1 }}>
      {messages
        .filter((msg) => msg.role !== 'tool')
        .map((msg) => (
          <ChatMessage
            key={msg.id ?? `${msg.role}-${msg.createdAt}`}
            message={msg}
            conversationId={conversationId}
            onActionResolved={onActionResolved}
          />
        ))}
    </Box>
  );
}
